import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { STATUS_LABEL, STATUS_STYLES, findLabel, APPLIANCE_TYPES, formatDateTime } from "@/lib/options";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";

export const STATUS_DOT = {
  new: "bg-red-500",
  in_progress: "bg-yellow-400",
  pending_approval: "bg-yellow-400",
  completed: "bg-green-500",
  cancelled: "bg-slate-400",
  declined: "bg-slate-400",
  expired: "bg-slate-300",
};
export const dotClass = (s) => STATUS_DOT[s] || "bg-slate-400";
export const isClosed = (s) => ["completed", "cancelled", "declined", "expired"].includes(s);

const pad = (n) => String(n).padStart(2, "0");
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function ScheduleCalendar() {
  const [anchor, setAnchor] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [jobs, setJobs] = useState([]);
  const [selected, setSelected] = useState(() => new Date().toDateString());
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (monthStart) => {
    setLoading(true);
    try {
      const last = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0);
      const { data } = await api.get(`/tickets/schedule/range?start=${ymd(monthStart)}&end=${ymd(last)}`);
      setJobs(data.items);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(anchor); }, [anchor, load]);

  const byDay = useMemo(() => {
    const m = {};
    for (const t of jobs) {
      if (!t.scheduled_at) continue;
      const key = new Date(t.scheduled_at).toDateString();
      (m[key] = m[key] || []).push(t);
    }
    return m;
  }, [jobs]);

  const cells = useMemo(() => {
    const firstDow = anchor.getDay();
    const daysInMonth = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0).getDate();
    const out = [];
    for (let i = 0; i < firstDow; i++) out.push(null);
    for (let d = 1; d <= daysInMonth; d++) out.push(new Date(anchor.getFullYear(), anchor.getMonth(), d));
    while (out.length % 7 !== 0) out.push(null);
    return out;
  }, [anchor]);

  const todayKey = new Date().toDateString();
  const monthLabel = anchor.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  const selectedJobs = byDay[selected] || [];
  const goMonth = (delta) => setAnchor((a) => new Date(a.getFullYear(), a.getMonth() + delta, 1));
  const goToday = () => {
    const d = new Date();
    setAnchor(new Date(d.getFullYear(), d.getMonth(), 1));
    setSelected(d.toDateString());
  };

  return (
    <div className="space-y-4" data-testid="schedule-calendar">
      <Card className="border-slate-200">
        <CardContent className="p-4 sm:p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="font-heading font-bold text-lg text-slate-900" data-testid="calendar-month-label">{monthLabel}</div>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" onClick={() => goMonth(-1)} data-testid="calendar-prev-month"><ChevronLeft className="w-4 h-4" /></Button>
              <Button variant="outline" size="sm" onClick={goToday} data-testid="calendar-today-btn">Today</Button>
              <Button variant="outline" size="sm" onClick={() => goMonth(1)} data-testid="calendar-next-month"><ChevronRight className="w-4 h-4" /></Button>
            </div>
          </div>
          <div className="grid grid-cols-7 text-center text-[11px] uppercase tracking-wider text-slate-500 mb-1">
            {WEEKDAYS.map((w) => <div key={w} className="py-1">{w}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-px bg-slate-200 rounded-md overflow-hidden border border-slate-200">
            {cells.map((d, i) => {
              if (!d) return <div key={`e${i}`} className="bg-slate-50 min-h-[64px] sm:min-h-[88px]" />;
              const key = d.toDateString();
              const dayJobs = byDay[key] || [];
              const isToday = key === todayKey;
              const isSel = key === selected;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setSelected(key)}
                  className={`bg-white min-h-[64px] sm:min-h-[88px] p-1 sm:p-1.5 text-left align-top flex flex-col gap-1 transition-colors hover:bg-blue-50/60 ${isSel ? "ring-2 ring-inset ring-blue-500" : ""}`}
                  data-testid={`calendar-day-${ymd(d)}`}
                >
                  <span className={`text-xs font-medium w-5 h-5 flex items-center justify-center rounded-full ${isToday ? "bg-blue-600 text-white" : "text-slate-700"}`}>
                    {d.getDate()}
                  </span>
                  <div className="hidden sm:flex flex-col gap-0.5 overflow-hidden">
                    {dayJobs.slice(0, 3).map((t) => (
                      <span key={t.id} className={`flex items-center gap-1 text-[10px] text-slate-700 truncate ${isClosed(t.status) ? "opacity-60" : ""}`}>
                        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${dotClass(t.status)}`} />
                        <span className="truncate">{t.customer ? t.customer.last_name : "Job"}</span>
                      </span>
                    ))}
                    {dayJobs.length > 3 && <span className="text-[10px] text-slate-500">+{dayJobs.length - 3} more</span>}
                  </div>
                  <div className="flex sm:hidden flex-wrap gap-0.5">
                    {dayJobs.slice(0, 4).map((t) => (
                      <span key={t.id} className={`w-1.5 h-1.5 rounded-full ${dotClass(t.status)}`} />
                    ))}
                    {dayJobs.length > 4 && <span className="text-[9px] text-slate-500 leading-none">+</span>}
                  </div>
                </button>
              );
            })}
          </div>
          {loading && <div className="text-xs text-slate-400 mt-2">Loading…</div>}
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardContent className="p-5" data-testid="calendar-day-detail">
          <div className="font-heading font-bold text-slate-900 mb-3">
            {new Date(selected).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric", year: "numeric" })}
          </div>
          {selectedJobs.length === 0 && (
            <div className="text-sm text-slate-500 py-4 text-center">No jobs this day.</div>
          )}
          <div className="divide-y divide-slate-100">
            {selectedJobs.map((t) => (
              <Link
                key={t.id}
                to={`/tickets/${t.id}`}
                className={`flex items-center justify-between py-3 hover:bg-slate-50 -mx-2 px-2 rounded-md transition-colors ${isClosed(t.status) ? "opacity-60" : ""}`}
                data-testid={`calendar-job-${t.id}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className={`w-3 h-3 rounded-full shrink-0 ${dotClass(t.status)}`} title={STATUS_LABEL[t.status]} />
                  <div className="min-w-0">
                    <div className={`font-medium text-slate-900 truncate ${t.status === "completed" ? "line-through decoration-slate-300" : ""}`}>
                      {t.customer ? `${t.customer.first_name} ${t.customer.last_name}` : "—"}
                    </div>
                    <div className="text-xs text-slate-500 truncate">
                      {findLabel(APPLIANCE_TYPES, t.appliance_type)} · {t.customer?.phone}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <Badge variant="outline" className={`${STATUS_STYLES[t.status]} rounded-full hidden sm:inline-flex`}>{STATUS_LABEL[t.status]}</Badge>
                  <div className="text-sm text-slate-600 font-medium">{formatDateTime(t.scheduled_at)}</div>
                </div>
              </Link>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
